// readable-export.ts — turn the journal into something a person would actually want to read.
//
// We already have `backup.ts`: a faithful JSON envelope that restores byte-for-byte. That is the
// right tool for moving between devices, and the wrong one for "I want my diary". The third-most
// common complaint about the journals people PAY for is not being able to get their writing out in
// a form they can read, print or keep (23 of 140 negative reviews, 2026-08-11).
//
// So this is deliberately NOT a backup: it is a keepsake. Markdown or plain text, built entirely on
// the device, nothing sent anywhere.

import type { JournalEntry } from "./store";

export type ReadableFormat = "markdown" | "text";

const MOOD_WORD: Record<number, string> = {
  1: "rough",
  2: "low",
  3: "steady",
  4: "good",
  5: "great",
};

/** "Monday, 11 August 2026" — the customer's own locale, in their own timezone. */
function longDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function timeOfDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** "2026" → the year heading; entries are grouped so a long journal reads like a book. */
function yearOf(iso: string): string {
  return iso.slice(0, 4) || "Undated";
}

export function buildReadable(entries: JournalEntry[], format: ReadableFormat): string {
  // Oldest first: a journal reads forwards, even though the app lists newest first.
  const ordered = [...entries].sort((a, b) => a.created_at.localeCompare(b.created_at));
  const md = format === "markdown";
  const out: string[] = [];

  const first = ordered[0]?.created_at;
  const last = ordered[ordered.length - 1]?.created_at;
  const span = first && last ? `${longDate(first)} — ${longDate(last)}` : "";

  out.push(md ? "# My journal" : "MY JOURNAL");
  if (span) out.push(span);
  out.push(`${ordered.length} ${ordered.length === 1 ? "entry" : "entries"}`);
  out.push("");

  let currentYear = "";
  for (const e of ordered) {
    const y = yearOf(e.created_at);
    if (y !== currentYear) {
      currentYear = y;
      out.push(md ? `\n## ${y}` : `\n${y}\n${"=".repeat(y.length)}`);
      out.push("");
    }

    const when = `${longDate(e.created_at)}${timeOfDay(e.created_at) ? ` · ${timeOfDay(e.created_at)}` : ""}`;
    const mood = MOOD_WORD[e.mood] ? ` · felt ${MOOD_WORD[e.mood]}` : "";
    const title = (e.title || "").trim();

    if (md) {
      out.push(`### ${title || when}`);
      out.push(`*${when}${mood}*`);
    } else {
      out.push(title || when);
      out.push(`${when}${mood}`);
      out.push("-".repeat(Math.min(60, Math.max(when.length, title.length))));
    }
    out.push("");
    out.push((e.body || "").trim());
    if (e.photo_ref) {
      // The photo itself stays on the device — say so rather than emit a broken link.
      out.push("");
      out.push(md ? "*(a photo is attached to this entry, kept on your device)*"
                  : "(a photo is attached to this entry, kept on your device)");
    }
    out.push("");
    if (!md) out.push("");
  }

  out.push("");
  out.push(md ? "---" : "—");
  out.push("Written in Oriora Journal. Your entries live on your device.");
  return out.join("\n");
}

export function readableFilename(format: ReadableFormat): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `my-journal-${stamp}.${format === "markdown" ? "md" : "txt"}`;
}
